// -- Instrument --

export interface Instrument {
    title_id: number
    iid: string
    currency: string
    deleted: number
}

export type PositionType = "BUY" | "SELL" | "FLAT"
export enum OpenOrderPositionType {
    BUY = "BUY",
    SELL = "SELL",
}

export const positionTypeOptions = [
    { label: "BOTH", value: null },
    ...Object.entries(OpenOrderPositionType).map(([label, value]) => {
        return { label, value }
    }),
]

// -- MissedSignal --
export interface MissedSignal {
    reason: string
    trigger_ts: number
    title_id: number
    signal_id: number
    position: PositionType
}

// -- MissedQuote --
export interface MissedQuote {
    t: number
    title_id: number
}

export interface Missing {
    signals: MissedSignal[]
    quotes: MissedQuote[]
}

export interface Execution {
    size: number
    price: number
    timestamp: number
}

export type OrderType = "MKT" | "LMT" | "STP" | "TST" | "TRL" | "TKP" | "OPE"

export type OrderDefinitionType = "OPEN" | "STOP"

export type LabeledOrderType = {
    label: string
    value: OrderType
    type: OrderDefinitionType
}

export const orderTypeOptions: LabeledOrderType[] = [
    { label: "Market", value: "MKT", type: "OPEN" },
    { label: "Limit", value: "LMT", type: "OPEN" },
    { label: "Stop Loss", value: "STP", type: "STOP" },
    { label: "Trailing Stop", value: "TRL", type: "STOP" },
    { label: "Time Stop", value: "TST", type: "STOP" },
    // { label: 'Take Profit', value: 'TKP', type: 'STOP' }
]

// -- ReferencedPriceOptions --
export type ReferenceType = "SIGNAL" | "FORCED" | "DAILY_VOLA" | "YEARLY_VOLA"

export type LabeledReferenceType = {
    label: string
    value: ReferenceType
}

// Define the stopTypeOptions with labels
export const referenceTypes: LabeledReferenceType[] = [
    { label: "Custom", value: "FORCED" },
    { label: "Signal", value: "SIGNAL" },
    { label: "Daily Vola", value: "DAILY_VOLA" },
    { label: "Yearly Vola", value: "YEARLY_VOLA" },
]

// -- ReferencedPriceOptions --
export type ReferencedPriceOptions =
    | "OPEN"
    | "SIGNAL"
    | "HIGH_52_WEEKS"
    | "LOW_52_WEEKS"
    | "SMA_50"
    | "SMA_200"
    | "SMA_CUSTOM"

export type LabeledReferencedPriceOptions = {
    label: string
    value: ReferencedPriceOptions
}

// Define the stopTypeOptions with labels
export const referencedPriceOptions: LabeledReferencedPriceOptions[] = [
    { label: "Fill Price", value: "OPEN" },
    { label: "Signal", value: "SIGNAL" },
    { label: "52 Weeks High", value: "HIGH_52_WEEKS" },
    { label: "52 Weeks Low", value: "LOW_52_WEEKS" },
    { label: "SMA 50", value: "SMA_50" },
    { label: "SMA 200", value: "SMA_200" },
    { label: "SMA Custom", value: "SMA_CUSTOM" },
]

export type TradedInstrumentType = "UNDERLYING_ASSET" | "OPTION"
export type LabeledTradedInstrumentTypeOptions = {
    label: string
    value: TradedInstrumentType
}
export const tradedInstrumentOptions: LabeledTradedInstrumentTypeOptions[] = [
    { label: "As Signal", value: "UNDERLYING_ASSET" },
    { label: "Option", value: "OPTION" },
]

export type OptionType = "CALL" | "PUT"
export type LabeledOptionTypeOptions = {
    label: string
    value: OptionType
    disable?: boolean
}
export const optionTypeOptions: LabeledOptionTypeOptions[] = [
    { label: "PUT", value: "PUT" },
    { label: "CALL", value: "CALL" },
]

export type ExerciseStyle = "EUROPEAN" | "AMERICAN"
export type LabeledExerciseStyleOptions = {
    label: string
    value: ExerciseStyle
    disable?: boolean
}
export const exerciseStyleOptions: LabeledExerciseStyleOptions[] = [
    { label: "European", value: "EUROPEAN" },
    { label: "American", value: "AMERICAN", disable: true },
]

export type ExecuteType = "EXACT" | "AVG"

export type LabeledExecuteTypeOptions = {
    label: string
    value: ExecuteType
}

export const executeTypeOptions: LabeledExecuteTypeOptions[] = [
    { label: "Exact", value: "EXACT" },
    { label: "Incl. random", value: "AVG" },
]

export type LimitType = "LAST" | "PREV_CLOSE"

export type LabeledLimitTypeOptions = {
    label: string
    value: LimitType
}

export const limitTypeOptions: LabeledLimitTypeOptions[] = [
    { label: "Last", value: "LAST" },
    { label: "Prev. Close", value: "PREV_CLOSE" },
]

export type TargetType = "PERCENT" | "FORCED"

export interface TradedInstrumentTemplate {
    type: TradedInstrumentType
}

export const defaultUnderlyingAssetTemplate: TradedInstrumentTemplate = {
    type: "UNDERLYING_ASSET",
}

export interface OptionTemplate extends TradedInstrumentTemplate {
    option_type: OptionType
    exercise_style: ExerciseStyle
    strike_price_rate: number
    expiring_in_days: number
}

export function isInstanceOfOptionTemplate(obj: unknown): obj is OptionTemplate {
    return (
        obj !== undefined &&
        obj !== null &&
        typeof obj === "object" &&
        "option_type" in obj &&
        typeof obj.option_type === "string" &&
        ["CALL", "PUT"].includes(obj.option_type) &&
        "exercise_style" in obj &&
        typeof obj.exercise_style === "string" &&
        "strike_price_rate" in obj &&
        typeof obj.strike_price_rate === "number" &&
        "expiring_in_days" in obj &&
        typeof obj.expiring_in_days === "number"
    )
}

export interface UnderlyingAsset {
    underlying_title_id: number
    type: TradedInstrumentType
}

export interface Option {
    underlying_title_id: number
    type: TradedInstrumentType
    option_type: OptionType
    exercise_style: ExerciseStyle
    strike_price: number
    expiration_date: string
}

export const defaultOptionTemplate: OptionTemplate = {
    type: "OPTION",
    option_type: "PUT",
    exercise_style: "EUROPEAN",
    strike_price_rate: 0.1,
    expiring_in_days: 30,
}

export type TradedInstrument = UnderlyingAsset | Option

export function isInstanceOfOption(obj: TradedInstrument | undefined): obj is Option {
    return (
        obj !== undefined &&
        "option_type" in obj &&
        "exercise_style" in obj &&
        typeof obj.exercise_style === "string" &&
        "strike_price" in obj &&
        typeof obj.strike_price === "number" &&
        "expiration_date" in obj &&
        typeof obj.expiration_date === "string"
    )
}

export interface OrderTemplate {
    type: OrderType
    traded_instrument_type: TradedInstrumentType
    traded_instrument: TradedInstrumentTemplate | OptionTemplate
    referenced_price_options: ReferencedPriceOptions
    execute_minutes: number
    execute_type: ExecuteType
}

export interface SignalOrderTemplate extends OrderTemplate {
    is_counter_position?: boolean
    signal_type: OpenOrderPositionType | null
}

export interface MarketOrderTemplate extends SignalOrderTemplate {
    type: "MKT"
}

export interface LimitOrderTemplate extends SignalOrderTemplate {
    type: "LMT"
    limit_open: number
    limit_type: LimitType
}

export interface BasicTrackingOrderTemplate extends OrderTemplate {
    stop_type: ReferenceType
    valid_ms?: number
}

export interface WithTarget {
    target_reference_type: ReferenceType
    target_factor: number
    target_rate: number
}

export interface WithWait {
    wait_ms_reference_type: ReferenceType
    wait_ms: number
}

export type None<T> = { [K in keyof T]?: never }
export type TrackingOrderTemplate =
    | (BasicTrackingOrderTemplate & None<WithTarget> & None<WithWait>)
    | (BasicTrackingOrderTemplate & WithTarget & None<WithWait>)
    | (BasicTrackingOrderTemplate & None<WithTarget> & WithWait)
    | (BasicTrackingOrderTemplate & WithTarget & WithWait)

export type TimeStopOrderTemplate = TrackingOrderTemplate & {
    type: "TST"
}

export type StopLossOrderTemplate = TrackingOrderTemplate & {
    type: "STP"
    stop_factor: number
    stop_rate: number
}

export type TrailingStopOrderTemplate = TrackingOrderTemplate &
    Omit<StopLossOrderTemplate, "type"> & {
        type: "TRL"
        referenced_price_options_sma_days?: number
    }

export type OrderDefinition =
    | MarketOrderTemplate
    | LimitOrderTemplate
    | StopLossOrderTemplate
    | TrailingStopOrderTemplate
    | TimeStopOrderTemplate

export const defaultMarketOrderTemplate: MarketOrderTemplate = {
    type: "MKT",
    traded_instrument_type: "UNDERLYING_ASSET",
    execute_minutes: 1,
    execute_type: "EXACT",
    referenced_price_options: "OPEN",
    signal_type: null,
    traded_instrument: { type: "UNDERLYING_ASSET" },
    is_counter_position: false,
}

export const defaultTrackingOrderTemplate: StopLossOrderTemplate = {
    type: "STP",
    traded_instrument_type: "UNDERLYING_ASSET",
    traded_instrument: defaultUnderlyingAssetTemplate,
    execute_minutes: 1,
    referenced_price_options: "OPEN",
    execute_type: "EXACT",
    stop_type: "FORCED",
    stop_rate: 0,
    stop_factor: 1,
}

export function isMarketOrder(od: OrderDefinition): od is MarketOrderTemplate {
    return od.type === "MKT"
}

export function isLimitOrder(od: OrderDefinition | SignalOrderTemplate): od is LimitOrderTemplate {
    return od.type === "LMT"
}

export function isStopLossOrder(od: OrderDefinition): od is StopLossOrderTemplate {
    return od.type === "STP"
}

export function isTrailingStopOrder(od: OrderDefinition): od is TrailingStopOrderTemplate {
    return od.type === "TRL"
}

export function isTimeStopOrder(od: OrderDefinition): od is TimeStopOrderTemplate {
    return od.type === "TST"
}

export function isWithWait(od: OrderDefinition): od is OrderDefinition & WithWait {
    return "wait_ms_reference_type" in od
}

export function isWithTarget(od: OrderDefinition): od is OrderDefinition & WithTarget {
    return "target_reference_type" in od
}

export interface Order {
    cost: number
    executions: Execution[]
    size: number
    price: number
    title_id: number
    order_definition: OrderDefinition
    good_until_date: number
    position: PositionType
    is_counter_position: boolean
    timestamp: number
    good_after_date: number
    relative_target?: number
    absolute_target?: number
    target_reference_price?: number
    target_reached_at?: number
    relative_stop?: number
    absolute_stop?: number
    stop_reference_price?: number
    stop_reached_at?: number
    moving_price?: number
    moving_price_history?: number[]
}

interface Dividend {
    value: number
    tax: number
    timestamp: number
}

export interface Signal {
    signal_id: number
    title_id: number
    trigger_ts: number
    position: PositionType
    stop: number
    target: number
    referenced_price: number
    wait_ms: number | null
    allocation_factor: number
}

export type SignalInput = Omit<Signal, "signal_id">

export type SignalInputRaw = Omit<SignalInput, "trigger_ts"> & { trigger_ts: number | string }

export interface Trade {
    identifier: string
    included_signals: Signal[]
    traded_instrument: TradedInstrument
    open_order: Order
    max_price: { v: number; t: number }
    close_order: Order
    min_price: { v: number; t: number }
    included_dividends: null | Dividend[]
}

export interface DividendDisplay {
    time: string
    title: string
    signal: string
    perUnit: string
    size: string
    value: string
    tax: string
}

export interface HeldPosition {
    identifier: string
    size: number
    price: number
    title_id: number
    isin: string
}

export interface Characteristics {
    first_val: number
    num_titles: number
    return_pa: number
    return_d: number
    hit_ratio_buy: number
    last_val: number
    downside_vola: number
    hit_ratio: number
    return_cum: number
    max_dd: number
    first_ts: number
    last_ts: number
    vola: number
    hit_ratio_sell: number
    sharpe: number
    sortino: number
    year_perf: { [key: string]: number }
    alpha?: number
    beta?: number
    correlation?: number
    r2?: number
}

export type CharacteristicsWithYearPerf = Characteristics & {
    backtest_id: string
    year_perf: { [key: string]: number }
}

export interface BacktestPeriodResult {
    characteristics: Characteristics
    missing: Missing
    trades: { [signalId: string]: Trade[] }
    positions: { [timestamp: string]: HeldPosition[] }
    titles: { [titleId: number]: string }
}

export type BacktestResultProp = {
    included_titles?: Instrument[]
} & Record<string, BacktestPeriodResult>

interface TitleResultProps {
    start_date: number
    end_date: number
    reference_backtest_id: string
    title_id: number
}

export interface BacktestResult {
    result: BacktestResultProp
    error?: string
    success: boolean
    backtest_id: string
    is_title_result?: boolean
    is_mixed_strategy?: boolean
    title_result_props?: TitleResultProps
}

export function isInstanceOfBacktestPeriodResult(
    obj: BacktestPeriodResult | Instrument[] | undefined
): obj is BacktestPeriodResult {
    return (
        obj !== undefined &&
        "characteristics" in obj &&
        "missing" in obj &&
        typeof obj.missing === "object" &&
        "trades" in obj &&
        typeof obj.trades === "object" &&
        "positions" in obj &&
        typeof obj.positions === "object" &&
        "titles" in obj &&
        typeof obj.titles === "object"
    )
}

export interface TradeData {
    trades: Record<number, Trade>
    titles: Record<number, string>
}

export interface SignalDisplay {
    id: number | string // signal.signal_id
    title_name: string // titles[signal.title_id] || 'Unknown'
    reason: string // signal.reason
    trigger_time: string // formatted date string
    position: string | number // signal.position (depends on your data)
}

export type StrikingMethod = "AVG" | "WORST" | "BEST" | "OPEN" | "CLOSE" | "MIDDLE"

export type StrikingMethodOption = {
    label: string
    value: StrikingMethod
}

export const strikingMethodOptions: StrikingMethodOption[] = [
    { label: "Average", value: "AVG" },
    { label: "Best", value: "WORST" },
    { label: "Worst", value: "BEST" },
    { label: "Open", value: "OPEN" },
    { label: "Close", value: "CLOSE" },
    { label: "Middle High/Low", value: "MIDDLE" },
]

export type ActionOnNewSignal = "PORTFOLIO" | "PORTFOLIO_ADJUST" | "TURN" | "HOLD" | "ADJUST" | "IGNORE"
export type AllocationOption = "EQUAL_WEIGHT" | "FROM_SIGNAL" | "FIXED_GLOBAL" | "FIXED_AMOUNT"

export type LabeledAllocationOption = {
    label: string
    value: AllocationOption
}

export const allocationOptions: LabeledAllocationOption[] = [
    { label: "Equal Weight", value: "EQUAL_WEIGHT" },
    { label: "From Signal", value: "FROM_SIGNAL" },
    { label: "Fixed Factor", value: "FIXED_GLOBAL" },
    { label: "Fixed Amount", value: "FIXED_AMOUNT" },
]

export type BacktestRequest = {
    signal_group_id?: number
    signals: SignalInput[]
    policies: {
        action_on_new_signal: ActionOnNewSignal
        allocation_factor?: number
        allocation_option: AllocationOption
        amount: number
        fixed_amount_new_positions?: number
        risk_free_rate?: number
        close_before_time_gaps: boolean
        cost: number
        ignore_signals_from_different_date: boolean
        overnight_holding: boolean
        period_ms: number
        period_profit_goal: number
        period_stop_loss: number
        rolling_period_checks: boolean
        striking_method: StrikingMethod
        trading_hours_start: string
        trading_hours_stop: string
        use_daily_quotes: boolean
        use_real_option_prices: boolean
        start_date?: string
    }
    tz_name: string
    open_orders: SignalOrderTemplate[]
    tracking_orders: TrackingOrderTemplate[]
    strike_price_intervals?: Array<{
        lower_bound_underlying_price: number
        upper_bound_underlying_price: number
        price_interval: number
    }>
}

export type StrikePriceInterval = {
    id: string
    lowerBoundUnderlyingPrice: number
    upperBoundUnderlyingPrice: number
    priceInterval: number
}

export interface PolicySettings {
    backtest_id?: string
    rollingPeriodChecks: boolean
    periodMs: number
    periodProfitGoal: number
    periodStopLoss: number
    overnightHolding: boolean
    closeBeforeTimeGaps: boolean
    ignoreSignalsFromDifferentDate: boolean
    allocationFactor?: number
    allocationOption: AllocationOption
    fixedAmountNewPositions?: number
    amount: number
    cost: number
    riskFreeRate?: number
    strikingMethod: StrikingMethod
    actionOnNewSignal: ActionOnNewSignal
    tradingHoursStart: string
    tradingHoursStop: string
    timezoneName: string
    useDailyQuotes: boolean
    useRealOptionPrices: boolean
    openOrders: SignalOrderTemplate[]
    trackingOrders: TrackingOrderTemplate[]
    strikePriceIntervals: StrikePriceInterval[]
    startDate?: string
}

export const policyLabels: Record<keyof PolicySettings, string> = {
    backtest_id: "Backtest ID",
    rollingPeriodChecks: "Rolling Period Checks",
    periodMs: "Period (ms)",
    periodProfitGoal: "Period Profit Goal",
    periodStopLoss: "Period Stop Loss",
    overnightHolding: "Overnight Holding",
    closeBeforeTimeGaps: "Close Before Time Gaps",
    ignoreSignalsFromDifferentDate: "Ignore Signals From Different Date",
    allocationFactor: "Allocation Factor (%)",
    allocationOption: "Allocation Option",
    fixedAmountNewPositions: "Fixed Amount ($)",
    amount: "Capital",
    cost: "Cost/Trade Var",
    riskFreeRate: "Risk-Free Rate",
    strikingMethod: "Execution Simulation",
    actionOnNewSignal: "Action On New Signal",
    tradingHoursStart: "Trading Hours Start",
    tradingHoursStop: "Trading Hours Stop",
    timezoneName: "Timezone",
    useDailyQuotes: "Use Daily Quotes",
    useRealOptionPrices: "Option Prices",
    openOrders: "Open Orders",
    trackingOrders: "Stop Orders",
    strikePriceIntervals: "Strike Price Ranges",
    startDate: "Start date",
}

export interface Settings {
    strategyName: string
    policies: PolicySettings
    relatedSignalGroupIds: number[]
}

export type PartialSettings = Omit<Settings, "policies"> & {
    policies: Partial<PolicySettings>
}
export type BacktestSignal = Partial<PolicySettings> & {
    signalGroupId: number
    descEn: string
    createdAt: number
    strategyName: string
}

export const defaultSettings: Settings[] = [
    {
        strategyName: "Aggressive Day Trading",
        relatedSignalGroupIds: [],
        policies: {
            rollingPeriodChecks: false,
            periodMs: 0,
            periodProfitGoal: 0,
            periodStopLoss: 0,
            overnightHolding: false,
            closeBeforeTimeGaps: true,
            ignoreSignalsFromDifferentDate: true,
            allocationOption: "EQUAL_WEIGHT",
            amount: 10000,
            cost: 0.1,
            strikingMethod: "CLOSE",
            actionOnNewSignal: "PORTFOLIO",
            tradingHoursStart: "09:00",
            tradingHoursStop: "17:00",
            timezoneName: "Europe/Berlin",
            useDailyQuotes: true,
            useRealOptionPrices: false,
            openOrders: [],
            trackingOrders: [],
            strikePriceIntervals: [],
        },
    },
    {
        strategyName: "Swing Trading Strategy",
        relatedSignalGroupIds: [],
        policies: {
            rollingPeriodChecks: true,
            periodMs: 72,
            periodProfitGoal: 0,
            periodStopLoss: 0,
            overnightHolding: true,
            closeBeforeTimeGaps: false,
            ignoreSignalsFromDifferentDate: false,
            allocationOption: "EQUAL_WEIGHT",
            amount: 25000,
            cost: 0.05,
            strikingMethod: "CLOSE",
            actionOnNewSignal: "PORTFOLIO",
            tradingHoursStart: "08:00",
            tradingHoursStop: "18:00",
            timezoneName: "Europe/London",
            useDailyQuotes: true,
            useRealOptionPrices: false,
            openOrders: [],
            trackingOrders: [],
            strikePriceIntervals: [],
        },
    },
]

export type MonthlyValues = Record<number, Record<number, number>>
export type PerformanceTable = Record<string, string[]>

export type MonthlyPerformance = {
    years: string[]
    tables: { [key: string]: PerformanceTable }
}

export type YearlyPerformance = {
    [key: string]: number
}

export type YearlyPerformances = {
    [key: string]: YearlyPerformance
}

export interface PerformanceDataPoint {
    time: number
    navValue: number
    indexValue: number
}

export interface PerformanceHistory {
    [key: string]: PerformanceDataPoint[]
}

export interface TradeDisplay {
    id: number
    title: string
    direction: string
    open_time: string
    open_price: string
    open_size: number
    open_type: string
    open_cost: number
    close_size: number
    close_time: string
    close_price: string
    close_type: string
    close_cost: number
    net_pnl: string
    net_pnl_perc: string
    instrument: string
}

export interface TitleTradeStats {
    title: string
    position: number
    sum: number
    num_trades: number
    gain_loss: number
}

export interface ExitCounts {
    MKT: number
    TRL: number
    STP: number
    LMT: number
    TST: number
    TKP: number
    OPE: number
}

export interface TradesStatsSummary {
    sumTrades: number
    maxGain: number
    maxLoss: number
    slippage: number
    exits: ExitCounts
}

export interface TitleTradesResult {
    titleTrades: TitleTradeStats[]
    stats: TradesStatsSummary
}

export interface PerformanceOverview {
    backtest_id: string
    return_cum: string
    return_pa: string
    vola: string
    downside_vola: string
    sharpe: string
    sortino: string
    max_dd: string
    hit_ratio: string
    hit_ratio_buy: string
    hit_ratio_sell: string
    alpha?: number
    beta?: number
    correlation?: number
    r2?: number
}

export const performanceOverviewTableStructure: {
    [property: string]: {
        label: string
        display: (value: string | number | undefined) => string
        colorIndicator: boolean
    }
} = {
    backtest_id: {
        label: "Test",
        display: (value: string | number | undefined): string => value + "",
        colorIndicator: false,
    },
    return_cum: {
        label: "Cum. Return",
        display: (value: string | number | undefined): string => value + "%",
        colorIndicator: true,
    },
    return_pa: {
        label: "Return p.a.",
        display: (value: string | number | undefined): string => value + "%",
        colorIndicator: true,
    },
    vola: {
        label: "Vola p.a.",
        display: (value: string | number | undefined): string => value + "%",
        colorIndicator: false,
    },
    downside_vola: {
        label: "Downside Vola p.a.",
        display: (value: string | number | undefined): string => value + "%",
        colorIndicator: false,
    },
    max_dd: {
        label: "Max DD",
        display: (value: string | number | undefined): string => value + "%",
        colorIndicator: false,
    },
    sharpe: {
        label: "Sharpe",
        display: (value: string | number | undefined): string => value + "",
        colorIndicator: false,
    },
    sortino: {
        label: "Sortino",
        display: (value: string | number | undefined): string => value + "",
        colorIndicator: false,
    },
    alpha: {
        label: "Alpha",
        display: (value: string | number | undefined): string => (value !== undefined ? value + "" : "-"),
        colorIndicator: true,
    },
    beta: {
        label: "Beta",
        display: (value: string | number | undefined): string => (value !== undefined ? value + "" : "-"),
        colorIndicator: false,
    },
    correlation: {
        label: "Correl",
        display: (value: string | number | undefined): string => (value !== undefined ? value + "" : "-"),
        colorIndicator: false,
    },
    r2: {
        label: "R2",
        display: (value: string | number | undefined): string => (value !== undefined ? value + "" : "-"),
        colorIndicator: false,
    },
}

export type StatisticData = {
    [year: number]: {
        positiveLongTrades: number
        positiveShortTrades: number
        negativeLongTrades: number
        negativeShortTrades: number
        neutralLongTrades: number
        neutralShortTrades: number
    }
    exitByMarketOrder: number
    exitByTrailingStop: number
    exitByStopLoss: number
    positiveTrades: number
    negativeTrades: number
    neutralTrades: number
    positiveLongTrades: number
    negativeLongTrades: number
    neutralLongTrades: number
    positiveShortTrades: number
    negativeShortTrades: number
    neutralShortTrades: number
    sumReturnPositiveTrades: number
    sumReturnNegativeTrades: number
    sumReturnOfPositiveTrades: number
    sumReturnOfNegativeTrades: number
    avgGrossReturnOfPositiveTrades: number
    avgGrossReturnOfPositiveTradesPerc: number
    avgGrossReturnOfNegativeTrades: number
    avgGrossReturnOfNegativeTradesPerc: number
    positiveTradesInLast5Trades: number
    positiveTradesInLast10Trades: number
    positiveTradesInLast20Trades: number
    positiveTradesInLast50Trades: number
    largestPositiveTrade: number
    largestPositiveTradePerc: number
    largestPositiveTradeId: string | null
    largestNegativeTrade: number
    largestNegativeTradePerc: number
    largestNegativeTradeId: string | null
    sumHoldingTime: number
    numberOfTradingDays: number
    sumCosts: number
    sumDividends: number
    sumTaxes: number
    positiveTradesWithDividends: number
    negativeTradesWithDividends: number
    avgMaxVirtualProfitOfPositiveTrades: number
    avgMaxVirtualProfitOfNegativeTrades: number
    avgMaxVirtualLossOfPositiveTrades: number
    avgMaxVirtualLossOfNegativeTrades: number
    avgDiffToMaxProfitOfPositiveTrades: number
    avgDiffToMaxLossOfNegativeTrades: number
    tradeYears: number[]
    netProfit: number
    totalTrades: number
    totalLongTrades: number
    totalShortTrades: number
    averageHoldingTime: number
    profitTradePercentage: number
    lossTradePercentage: number
    longTradeWinPercentage: number
    shortTradeWinPercentage: number
    profitPercentage: number
    dividendPercentage: number
    hitRatioLast5: number
    hitRatioLast10: number
    hitRatioLast20: number
    hitRatioLast50: number
}

export interface BacktestStatistics {
    performanceOverview: PerformanceOverview[]
    performanceHistory: PerformanceHistory
    monthlyPerformance: MonthlyPerformance
    yearlyPerformances: YearlyPerformances
    dividends: DividendDisplay[]
    titleTrades: TitleTradesResult | null
    trades: TradeDisplay[]
    statistics: StatisticData
    missedSignals: SignalDisplay[]
}
